import { InfoIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { InternalLink } from '@components/link';
import { useModal } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import { useGetSSHKeysQuery, type Editor } from '@generated/graphql';
import type { ServerLaunchOption } from '@hooks/userPreferencesAtoms';
import { Divider, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { AddSSHKeyModal } from '@subsets/profile/keysTab';
import { CopySshLaunchCommandMenuItem } from './serverControls/CopySshLaunchCommandMenuItem';
import { launchServer } from './serverLaunchUtil';

type ServerLaunchOptionsMenuProps = Pick<
  Editor,
  'url' | 'editorId' | 'status' | 'sshPort' | 'workspaceId' | 'serverPath'
> & {
  anchorEl: null | HTMLElement;
  setAnchorEl: (anchorEl: null | HTMLElement) => void;
};

export const ServerLaunchOptionsMenu = ({
  workspaceId,
  editorId,
  url,
  serverPath,
  status,
  sshPort,
  anchorEl,
  setAnchorEl,
}: ServerLaunchOptionsMenuProps) => {
  const { palette } = useTheme();

  const { data: sshKeyData } = useGetSSHKeysQuery();
  const hasSshKeys = sshKeyData?.getSSHKeys?.length > 0;

  const { showModal: showAddSSHKeyModal } = useModal({
    component: AddSSHKeyModal,
    modalProps: { title: 'Add SSH Key' },
  });

  const handleAddSSHKey = () => {
    showAddSSHKeyModal();
  };

  const tooltipTitle =
    status?.state.toLowerCase() !== 'running'
      ? 'Server is not running'
      : !hasSshKeys
        ? 'No SSH keys found'
        : !sshPort
          ? 'SSH port not found'
          : '';

  const editorLaunchDisabled = !hasSshKeys || status?.state.toLowerCase() !== 'running' || !sshPort;

  const onServerLaunch = (launchMethod: Omit<ServerLaunchOption, 'none'>) => {
    launchServer({
      url,
      serverPath,
      ide: launchMethod,
      workspaceId,
      editorId,
    });
    setAnchorEl(null);
  };
  return (
    <Menu
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem
        onClick={() => {
          onServerLaunch('browser');
        }}
      >
        <Typography variant="caption2">Launch server in browser</Typography>
      </MenuItem>
      <Divider sx={{ mx: 2, my: 2, borderColor: palette.menu.border }} />
      {!hasSshKeys && (
        <Stack
          sx={{
            mx: 4,
            my: 2,
            px: 3,
            py: 2,
            gap: 1,
            alignItems: 'center',
            backgroundColor: palette.warning.light,
            color: palette.warning.contrastText,
            border: `1px solid ${palette.warning.dark}`,
            borderRadius: 1,
          }}
        >
          <Group alignItems="center" gap={2}>
            <InfoIcon color={palette.warning.dark} size={20} />
            <Typography variant="caption2">SSH key required for external IDEs</Typography>
          </Group>
          <Typography
            variant="caption1"
            sx={{
              textDecoration: 'underline',
              color: palette.warning.dark,
              cursor: 'pointer',
              '&:hover': { color: 'inherit' },
            }}
            onClick={handleAddSSHKey}
          >
            Add SSH key
          </Typography>
        </Stack>
      )}
      <Tooltip variant="secondary" title={tooltipTitle}>
        <div>
          <MenuItem
            disabled={editorLaunchDisabled}
            onClick={() => {
              onServerLaunch('cursor');
            }}
          >
            <Typography variant="caption2">Launch server in Cursor</Typography>
          </MenuItem>
        </div>
      </Tooltip>
      <Tooltip variant="secondary" title={tooltipTitle}>
        <div>
          <MenuItem
            disabled={editorLaunchDisabled}
            onClick={() => {
              onServerLaunch('vscode');
            }}
          >
            <Typography variant="caption2">Launch server in VSCode</Typography>
          </MenuItem>
        </div>
      </Tooltip>
      <Tooltip variant="secondary" title={tooltipTitle}>
        <div>
          <MenuItem
            disabled={editorLaunchDisabled}
            onClick={() => {
              onServerLaunch('windsurf');
            }}
          >
            <Typography variant="caption2">Launch server in Windsurf</Typography>
          </MenuItem>
        </div>
      </Tooltip>
      <Divider sx={{ mx: 2, my: 2, borderColor: palette.menu.border }} />
      <CopySshLaunchCommandMenuItem editorId={editorId} />
      <InternalLink unstyled to="/profile">
        <MenuItem>
          <Typography variant="caption2">Set preferred IDE</Typography>
        </MenuItem>
      </InternalLink>
    </Menu>
  );
};
